import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import type { SyncEngineDeploymentInfo } from '@weave/schema-gen-ts/dist/schemas/integrations/sync-config-api/v1/message.pb';
import { useListArtifactsQuery } from '@frontend/api-sync-config';
import {
  Button,
  DropdownField,
  ModalControlModalProps,
  SpinningLoader,
  Text,
  useControlledField,
} from '@frontend/design-system';
import { ActionModal, ActionModalContent } from '../shared/action-modal';
import { getDisplayValue, normalizeArtifactOption } from '../shared/helpers';

const ARTIFACTS_PAGE_SIZE = 100;

interface PinEngineModalProps {
  engine?: SyncEngineDeploymentInfo;
  isSaving: boolean;
  modalProps: ModalControlModalProps;
  onConfirm: (engine: SyncEngineDeploymentInfo, versionTag: string) => void;
}

export const PinEngineModal = ({ modalProps, isSaving, ...contentProps }: PinEngineModalProps) => (
  <ActionModal modalProps={modalProps} isSaving={isSaving}>
    <PinEngineModalContent {...contentProps} isSaving={isSaving} onClose={modalProps.onClose} />
  </ActionModal>
);

type PinEngineModalContentProps = Omit<PinEngineModalProps, 'modalProps'> & { onClose: () => void };

const PinEngineModalContent = ({ engine, isSaving, onClose, onConfirm }: PinEngineModalContentProps) => {
  const [selectedVersionTag, setSelectedVersionTag] = useState('');
  const packageName = engine?.versionInfo?.packageName?.trim() ?? '';
  const versionFieldProps = useControlledField({
    type: 'dropdown',
    value: selectedVersionTag,
    onChange: setSelectedVersionTag,
  });

  const {
    data: artifactsData,
    isLoading: isLoadingArtifacts,
    isError: isArtifactsError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useListArtifactsQuery({
    request: { packageName, pageSize: ARTIFACTS_PAGE_SIZE },
    options: { enabled: packageName !== '' },
    httpOptions: { skipValidation: true },
  });

  const versionTags = useMemo(() => {
    const tags = new Set<string>();

    for (const page of artifactsData?.pages ?? []) {
      for (const artifact of page.artifacts ?? []) {
        const versionTag = normalizeArtifactOption(artifact)?.versionTag;
        if (versionTag) tags.add(versionTag);
      }
    }

    return Array.from(tags);
  }, [artifactsData]);

  return (
    <ActionModalContent
      title={`Pin ${engine?.syncEngineId ?? 'Engine'}`}
      onClose={onClose}
      isSaving={isSaving}
      confirmLabel='Pin Engine'
      savingLabel='Pinning...'
      confirmDisabled={!engine || !selectedVersionTag || selectedVersionTag === engine.versionInfo?.targetVersion}
      onConfirm={() => engine && onConfirm(engine, selectedVersionTag)}
    >
      <div css={summaryStyle}>
        <Text size='small' color='subdued'>
          Package
        </Text>
        <Text weight='bold'>{getDisplayValue(packageName)}</Text>
      </div>

      {isLoadingArtifacts ? (
        <div css={loadingStyle} role='status'>
          <SpinningLoader size='small' />
          <Text>Loading versions...</Text>
        </div>
      ) : isArtifactsError ? (
        <Text color='error' role='alert'>
          Unable to load versions for this package.
        </Text>
      ) : versionTags.length === 0 ? (
        <Text color='subdued'>No versions are available for this package.</Text>
      ) : (
        <DropdownField
          {...versionFieldProps}
          name='pin-engine-version'
          label='Version'
          placeholder='Select a version'
          disabled={isSaving}
        >
          {versionTags.map((versionTag) => (
            <DropdownField.Option key={versionTag} value={versionTag}>
              {versionTag}
            </DropdownField.Option>
          ))}
        </DropdownField>
      )}

      {hasNextPage && (
        <Button variant='tertiary' loading={isFetchingNextPage} disabled={isSaving} onClick={() => fetchNextPage()}>
          Load more versions
        </Button>
      )}
    </ActionModalContent>
  );
};

const summaryStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-xxs);
`;

const loadingStyle = css`
  display: flex;
  align-items: center;
  gap: var(--fabric-space-gap-sm);
  min-height: 80px;
`;
